import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RagaLadder } from "@/components/RagaLadder";

export default function SouthIndian5() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="fvSHOqtLwbw"
        title="Kadhalikkum Pennin — A.R. Rahman · Kaadhalan (1994)"
        caption="Listen to the bass first: a swung, four-bar riff in C minor that keeps circling C, G, B♭ and F."
      />

      <SISCard label="The big idea" title="The bass line IS the composition">
        <p>
          Most students learn a song from the melody down: sing it first, find the chords, then
          figure out the bass. This lesson works the other way around. Jason's chart starts with
          a <strong>four-bar bass line</strong>, marked "swing feel", and everything else is
          built on top of it.
        </p>
        <p>
          Jason explains why: <em>"Usually when we start with songs, the melody comes first. But
          in this case, the bass line comes first."</em> Until you can play the bass line cleanly,
          at tempo, with the correct swing feel, you do not yet know this song.
        </p>
        <p>
          The bass line circles four notes — <strong>C, G, B♭ and F</strong>: the root, 5th, ♭7
          and 4th of C minor — in quick 16th-note groups that jump up an octave (C to the C
          above, G to the G above). Over it, the chords simply alternate:
          <strong> Cm | B♭ | Cm | B♭</strong>.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="The loop under the melody · C minor (from Jason's chart)"
        caption="One chord per bar: Cm, B♭, Cm, B♭. B♭ is the chord on the 7th degree of C minor (the subtonic). No dominant G chord at all — the groove comes from the bass, not from a V–i cadence."
        chords={[
          { chord: "Cm",  rn: "i",   function: "Tonic", pitches: ["C3", "Eb3", "G3", "C4"] },
          { bar: true },
          { chord: "B♭",  rn: "VII", pitches: ["Bb2", "D3", "F3", "Bb3"] },
          { bar: true },
          { chord: "Cm",  rn: "i",   function: "Tonic", pitches: ["C3", "Eb3", "G3", "C4"] },
          { bar: true },
          { chord: "B♭",  rn: "VII", pitches: ["Bb2", "D3", "F3", "Bb3"] },
        ]}
      />

      <SISCard label="Natural minor or Dorian?" title="The note the tune leaves out">
        <p>
          C natural minor (C–D–E♭–F–G–A♭–B♭) has a flat 6th: A♭. Dorian mode
          (C–D–E♭–F–G–A–B♭) has a natural 6th: A. The difference is one note, but it changes
          the emotional colour noticeably.
        </p>
        <p>
          In the phrases on Jason's chart, the melody uses only six notes — C, D, E♭, F, G and
          B♭. The 6th never appears, and neither does a chord that contains it (Cm and B♭ have
          no A or A♭). So these phrases fit both C natural minor and C Dorian; the 6th is the one
          note that would decide between them. Jason leaves it open too: <em>"The key is C, but
          the scale could be minor. It could be Dorian."</em>
        </p>
      </SISCard>

      <SISCard label="The Michael Jackson connection" title="A bass line from the Thriller era">
        <p>
          Jason's first observation about the song: <em>"There's a bassline very similar to a
          Michael Jackson song."</em> He adds that it is a synthesized bass line — programmed on
          hardware rather than played by hand, as on many records of that era, Michael Jackson's
          included. Quincy Jones produced Michael Jackson's <em>Off the Wall</em>,
          <em> Thriller</em> and <em>Bad</em> — albums whose bass lines and grooves are still
          studied by pop producers everywhere.
        </p>
        <p>
          The specific technique: a short, punchy bass riff in the low register, played with a
          swung 16th-note feel. The bass does not just provide root notes — it <em>moves</em>,
          it has melodic interest, and it stays in the ear. This is what distinguishes a great
          bass line from a functional one.
        </p>
        <p>
          Kaadhalan was released in 1994, early in Rahman's film career — and the song wears that
          funk-pop bass proudly.
        </p>
      </SISCard>

      <RagaLadder
        title="Kharaharapriya — Sa = C (C Dorian)"
        tonic="C"
        steps={[
          { swara: "Sa",  pitch: "C4",  notes: "Tonic",                      flat: false },
          { swara: "Ri₂", pitch: "D4",  notes: "Major 2nd",                  flat: false },
          { swara: "Ga₂", pitch: "Eb4", notes: "Minor 3rd — the blues note", flat: true  },
          { swara: "Ma₁", pitch: "F4",  notes: "Perfect 4th",                flat: false },
          { swara: "Pa",  pitch: "G4",  notes: "Perfect 5th",                flat: false },
          { swara: "Dha₂",pitch: "A4",  notes: "Major 6th (Dorian) — Natabhairavi uses A♭ here", flat: false },
          { swara: "Ni₂", pitch: "Bb4", notes: "Minor 7th — ♭VII",          flat: true  },
          { swara: "Sa",  pitch: "C5",  notes: "Octave",                      flat: false },
        ]}
      />

      <SISCard label="Carnatic context" title="Kharaharapriya — the raga of Dorian">
        <p>
          <strong>Kharaharapriya</strong> is the 22nd Melakarta raga. Its formula in solfège is
          Sa–Ri₂–Ga₂–Ma₁–Pa–Dha₂–Ni₂: the equivalent of the Western Dorian mode. It is one of
          the most widely used ragas in Carnatic music.
        </p>
        <p>
          The same scale with A♭ instead of A natural (Dha₁ instead of Dha₂) becomes
          Natabhairavi (the 20th Melakarta) — the natural minor we encountered in Lesson 1's June
          Ponaal. The two sit in the same chakra, two numbers apart, separated by just that one
          note. Since this tune's phrases avoid the 6th, they sit comfortably in either.
        </p>
      </SISCard>

      <SISCallout label="Practise this — bass first, always">
        Set your left hand on the piano. Play C, G, B♭, F — one note per beat at a slow tempo.
        Four beats, four notes, loop it. That is the skeleton of the bass line. Now add the
        octave jumps from Jason's chart (C then the C above, G then the G above) and swing the
        16ths. Only then add the right hand: Cm for one bar, B♭ for the next. Do this for five
        minutes before you attempt the melody.
      </SISCallout>

      <SISInfluence artists={["Michael Jackson / Quincy Jones (Thriller)", "James Jamerson (Motown basslines)"]}>
        <p>
          Listen to the bass on Michael Jackson's <em>Thriller</em> album, produced by Quincy
          Jones, and to James Jamerson's Motown bass lines, to hear how a moving bass line can
          carry a whole song's groove.
        </p>
      </SISInfluence>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/05-kadhalikkum-pennin-page1.png"
        caption="Jason's chart — the four-bar bass line, marked 'swing feel': C, G, B♭ and F in 16th-note groups with octave jumps."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/05-kadhalikkum-pennin-page2.png"
        caption="Page 2 — the melody's rhythm and notes (only C D E♭ F G B♭) over the loop Cm | B♭ | Cm | B♭, with its triplet figures highlighted."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/05-kadhalikkum-pennin-page3.png"
        caption="Page 3 — a bass rhythm under Cm and B♭, and the highlighted triplet figure."
      />

      <SISTakeaway>
        The bass line is the most underrated element in most students' practice. Melody gets all
        the attention. Chords get all the theory. But the bass is what makes a song groove or
        drag, what makes a chord change feel inevitable or jarring. Kadhalikkum Pennin teaches
        you to hear bass first — to build from the ground up. Once you learn this approach,
        every song you analyse will reveal its bass skeleton to you, and every song you write
        will be stronger for it.
      </SISTakeaway>
    </LessonShell>
  );
}
